/* Services are built once at the root and read through this hook. No screen
   imports a concrete provider, which is what makes the mock↔SharePoint switch a
   one-line change rather than a search-and-replace. */

import { useMemo, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { DATA_MODE, siteId } from '../auth/msalConfig';
import { ServiceContext } from './context';
import type { ILedgerServices } from './IServices';
import { createMockServices } from './MockServices';
import { createSharePointServices } from './sharepoint/SharePointServices';

/* The context and the hook live in ./context, which imports nothing that needs
   a DOM. Re-exported here so every existing `from '../services/ServiceContext'`
   keeps working. */
export { ServiceContext, useServices } from './context';

export function ServiceProvider({ children }: { children: ReactNode }) {
  const { getToken, user } = useAuth();

  const services = useMemo<ILedgerServices>(() => {
    if (DATA_MODE !== 'sharepoint' || !user) return createMockServices();
    return createSharePointServices({
      getToken,
      siteId,
      currentUser: user,
      syncUrl: import.meta.env.VITE_SYNC_URL,
      onboardUrl: import.meta.env.VITE_ONBOARD_URL,
      hubClientId: import.meta.env.VITE_HUB_CLIENT_ID,
      /* The Training Matrix's site and lists, for an administrator's
         department scope. Three ids rather than one because Graph addresses a
         list by id and the site by a composite — and because a build pointed
         at a different tenant must be able to point these elsewhere without a
         rebuild of the service layer.

         Site and list together or neither: a half-configured site would fail
         at the write rather than at the control, which is the worst place to
         find out. The departments list is looked up by title and carries a
         default, so it is not part of that gate. */
      joinerSite: import.meta.env.VITE_ONBOARDING_SITE_ID
        ? {
          siteId: import.meta.env.VITE_ONBOARDING_SITE_ID,
          casesList: 'Onboarding_Cases',
          templatesList: 'Onboarding_RoleTemplates',
          settingsList: 'Onboarding_Settings',
        }
        : undefined,
      trainingSite:
        import.meta.env.VITE_TM_SITE_ID
        && import.meta.env.VITE_TM_EMPLOYEES_LIST_ID
          ? {
            siteId: import.meta.env.VITE_TM_SITE_ID,
            employeesListId: import.meta.env.VITE_TM_EMPLOYEES_LIST_ID,
            departmentsListTitle:
              import.meta.env.VITE_TM_DEPARTMENTS_LIST ?? 'TrainingApp_Departments',
          }
          : undefined,
    });
  }, [getToken, user]);

  return <ServiceContext.Provider value={services}>{children}</ServiceContext.Provider>;
}
